// services/axiosCreator.ts
import axios from 'axios';
import { refreshToken } from './auth'; // Keep this import
import { jwtDecode } from 'jwt-decode';
import { logout } from '../slices/auth/authSlice';
import { store } from '../store/store';
// import { store } from '../store/store'; // Not strictly needed here anymore
// import { logout } from '../slices/auth/authSlice'; // Actions dispatched in refreshToken service

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to check if the token is expired
const isTokenExpired = (token: string | null): boolean => {
  // Added null check
  if (!token) return true;
  try {
    const decodedToken = jwtDecode<{ exp: number }>(token); // Add type hint for payload
    const currentTime = Date.now() / 1000;
    return decodedToken.exp < currentTime;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return true; // Treat decoding error as expired
  }
};

let isRefreshing = false; // Prevent multiple refresh attempts
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = []; // Queue for failed requests

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('authToken');

    // If the token is expired, refresh it
    if (token && isTokenExpired(token)) {
      if (!isRefreshing) {
        isRefreshing = true;
        console.log('Token expired, refreshing...');
        const currentRefreshToken = localStorage.getItem('refreshToken');

        if (!currentRefreshToken) {
          console.log('No refresh token, cannot refresh.');
          isRefreshing = false;
          // store.dispatch(logout()); // Optionally dispatch logout directly
          // window.location.href = '/auth'; // Avoid redirect here
          return Promise.reject(new Error('No refresh token available.'));
        }

        try {
          const data = await refreshToken(currentRefreshToken);
          const newAuthToken = data?.token;

          if (newAuthToken) {
            console.log(
              'Refreshed token successfully via request interceptor.'
            );
            token = newAuthToken; // Update token for current request
            processQueue(null, token); // Process queue with new token
          } else {
            // Refresh service failed (and should have dispatched logout)
            console.log('Refresh token failed via request interceptor.');
            processQueue(new Error('Token refresh failed.'), null); // Reject queue
            // window.location.href = '/auth'; // Avoid redirect here
            return Promise.reject(new Error('Failed to refresh token.'));
          }
        } catch (error) {
          console.error(
            'Error during token refresh in request interceptor:',
            error
          );
          processQueue(error, null); // Reject queue on error
          // window.location.href = '/auth'; // Avoid redirect here
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Wait for the ongoing refresh attempt
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            if (newToken) {
              config.headers['Authorization'] = `Bearer ${newToken}`;
            }
            return config;
          })
          .catch((err) => {
            return Promise.reject(err); // Propagate refresh error
          });
      }
    }

    // Add token to headers if available
    if (token && config.headers) {
      // Added header check
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    // Handle 401/403 specifically, avoid retrying refresh requests or already retried requests
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry &&
      originalRequest.url !== 'auth/refresh' // Don't retry refresh itself
    ) {
      if (!isRefreshing) {
        // Check if not already refreshing
        isRefreshing = true;
        originalRequest._retry = true; // Mark as retried
        console.log('Received 401/403, attempting token refresh...');
        const currentRefreshToken = localStorage.getItem('refreshToken');

        if (!currentRefreshToken) {
          console.log('No refresh token, cannot refresh.');
          isRefreshing = false;
          store.dispatch(logout()); // Optionally dispatch logout directly
          return Promise.reject(error); // Reject original error
        }

        try {
          const data = await refreshToken(currentRefreshToken); // This service handles success/failure dispatches
          const newAuthToken = data?.token;

          if (newAuthToken) {
            console.log(
              'Refreshed token successfully via response interceptor.'
            );
            axiosInstance.defaults.headers.common['Authorization'] =
              `Bearer ${newAuthToken}`;
            originalRequest.headers['Authorization'] = `Bearer ${newAuthToken}`; // Update header for retry
            processQueue(null, newAuthToken); // Process queue
            isRefreshing = false;
            return axiosInstance(originalRequest); // Retry original request
          } else {
            // Refresh service failed (and should have dispatched logout)
            console.log('Refresh token failed via response interceptor.');
            processQueue(new Error('Token refresh failed.'), null); // Reject queue
            isRefreshing = false;
            store.dispatch(logout()); // Optionally dispatch logout directly
            return Promise.reject(error); // Reject original error
          }
        } catch (refreshError) {
          console.error(
            'Error during token refresh in response interceptor:',
            refreshError
          );
          processQueue(refreshError, null); // Reject queue
          isRefreshing = false;
          store.dispatch(logout()); // Optionally dispatch logout directly
          return Promise.reject(error); // Reject original error
        }
      } else {
        // Add request to queue while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axiosInstance(originalRequest); // Retry with new token
          })
          .catch((err) => {
            return Promise.reject(err); // Propagate refresh error
          });
      }
    }
    // For errors other than 401/403, just reject
    return Promise.reject(error);
  }
);

export default axiosInstance;
